import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { RouteTransitionLogo } from '../components/RouteTransitionLogo';
import { GoogleSignInButton } from '../components/GoogleSignInButton';
import { PasswordInput } from '../components/PasswordInput';
import { formatFirstName, formatLastLetters } from '../utils/nameFormat';
import { useBackTarget } from '../hooks/useBackTarget';
import { trackSignup, identifyAirbridgeUser } from '../utils/airbridge';
import { getPendingInviteId, getPendingHostListingInviteId, getPendingStaffInviteToken } from '../utils/deepLinkHandler';
import { persistAuthSession } from '../utils/authStorage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RegisterPage({ onLogin }) {
  const navigate = useNavigate();
  const goBack = useBackTarget();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (!firstName || !lastName || !password || !confirmPassword || !email) {
      toast.error('Please fill in all fields');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/register`, {
        first_name: firstName,
        last_name: lastName,
        email,
        password,
        role: 'guest',
      });
      await persistAuthSession(res.data.access_token, res.data.user);
      localStorage.setItem('pendingVerificationEmail', email);

      // Airbridge: identify + fire signup event. We attribute the signup to
      // any pending invite that drove this user to /register so the dashboard
      // can measure invite→signup conversion accurately. Precedence:
      //   venue_staff > host_to_host > host_to_guest
      // — staff invites are highest-priority because they're the most
      // specific (the recipient already chose to join a venue), then host→host
      // (the user clearly came from the host referral landing page), then
      // host→guest as a fallback.
      identifyAirbridgeUser(res.data.user);
      const pendingStaffInviteToken = getPendingStaffInviteToken();
      const pendingHostListingInviteId = getPendingHostListingInviteId();
      const pendingInviteId = getPendingInviteId();
      let inviteContext = null;
      if (pendingStaffInviteToken) {
        inviteContext = { inviteId: pendingStaffInviteToken, inviteType: 'venue_staff' };
      } else if (pendingHostListingInviteId) {
        inviteContext = { inviteId: pendingHostListingInviteId, inviteType: 'host_to_host' };
      } else if (pendingInviteId) {
        inviteContext = { inviteId: pendingInviteId, inviteType: 'host_to_guest' };
      }
      trackSignup({
        user: res.data.user,
        method: 'email',
        inviteContext,
      });

      toast.success('Account created! Please verify your email.');
      navigate('/verify-email', { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
      className="app-shell premium-page"
      style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: 'var(--app-bg)',
        position: 'relative',
        paddingLeft: 24, paddingRight: 24, paddingBottom: 40,
      }}
    >
      {/* Back arrow */}
      <button
        onClick={goBack}
        aria-label="Go back"
        data-testid="register-back-button"
        style={{ position: 'absolute', top: 'calc(env(safe-area-inset-top, 0px) + 20px)', left: 20, background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
      >
        <ChevronLeft size={26} color="var(--text)" strokeWidth={1.75} />
      </button>

      {/* Logo */}
      <div className="auth-fade-up" style={{ animationDelay: '0ms' }}>
        <RouteTransitionLogo width={120} height={108} dataTestId="register-shared-logo" style={{ marginTop: 52 }} />
      </div>

      {/* Heading */}
      <h1
        className="auth-fade-up"
        style={{
          fontFamily: "'Amarante', serif",
          fontSize: 28,
          fontWeight: 400,
          color: '#303030',
          textAlign: 'center',
          marginTop: 12,
          marginBottom: 24,
          animationDelay: '100ms',
        }}
      >
        Join the Guestlist
      </h1>

      {/* Form card */}
      <form
        id="register-form"
        onSubmit={handleSignUp}
        className="premium-card auth-fade-up"
        style={{ width: '100%', maxWidth: 360, padding: '28px 24px', animationDelay: '200ms' }}
      >
        {/* First + Last name row */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 6 }}>
          <div style={{ flex: 1 }}>
            <label htmlFor="reg-first" className="auth-label">First Name</label>
            <input
              id="reg-first"
              type="text"
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => setFirstName(formatFirstName(e.target.value))}
              placeholder="Louisa"
              data-testid="register-first-name-input"
              className="auth-input"
            />
          </div>
          <div style={{ flex: 1 }}>
            <label htmlFor="reg-last" className="auth-label">Last Name</label>
            <input
              id="reg-last"
              type="text"
              autoComplete="family-name"
              value={lastName ? `${lastName}.` : ''}
              onChange={(e) => setLastName(formatLastLetters(e.target.value))}
              onKeyDown={(e) => {
                if (e.key === 'Backspace') {
                  e.preventDefault();
                  setLastName((prev) => prev.slice(0, -1));
                }
              }}
              placeholder="K."
              data-testid="register-last-name-input"
              className="auth-input"
            />
          </div>
        </div>
        <p style={{ margin: '0 0 18px', fontFamily: "'Outfit', sans-serif", fontSize: 12, color: '#999999', lineHeight: 1.4 }}>
          For privacy, we only keep your initial (e.g. {lastName ? `${lastName}.` : 'K.'})
        </p>

        <div style={{ marginBottom: 16 }}>
          <label htmlFor="reg-email" className="auth-label">E-mail</label>
          <input
            id="reg-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@email.com"
            data-testid="register-email-input"
            className="auth-input"
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label htmlFor="reg-password" className="auth-label">Password</label>
          <PasswordInput
            id="reg-password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            dataTestId="register-password-input"
          />
        </div>

        <div>
          <label htmlFor="reg-confirm" className="auth-label">Confirm Password</label>
          <PasswordInput
            id="reg-confirm"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="••••••••"
            dataTestId="register-confirm-password-input"
          />
        </div>
      </form>

      {/* Primary action */}
      <motion.button
        type="submit"
        form="register-form"
        disabled={loading}
        whileHover={loading ? {} : { scale: 1.01 }}
        whileTap={loading ? {} : { scale: 0.99 }}
        data-testid="register-submit-button"
        className="auth-primary-btn auth-fade-up"
        style={{ maxWidth: 360, marginTop: 24, animationDelay: '300ms' }}
      >
        {loading ? 'Creating…' : 'Sign Up'}
      </motion.button>

      {/* Divider */}
      <div className="auth-divider auth-fade-up" style={{ maxWidth: 360, margin: '22px 0', animationDelay: '380ms' }}>
        or
      </div>

      {/* Continue with Google */}
      <GoogleSignInButton
        onLogin={onLogin}
        dataTestId="register-google-button"
        style={{ maxWidth: 360, animationDelay: '420ms' }}
      />

      {/* Sign in */}
      <p
        className="auth-fade-up"
        style={{ fontFamily: "'Outfit', sans-serif", fontSize: 14, color: '#666666', marginTop: 28, animationDelay: '480ms' }}
      >
        Already have an account?{' '}
        <button
          type="button"
          onClick={() => navigate('/login')}
          data-testid="register-signin-link"
          className="auth-link auth-link-accent"
        >
          Sign In
        </button>
      </p>
    </motion.div>
  );
}
